import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { PriceChangesChart } from '@design/charts/price-changes-chart/PriceChangesChart';
import { EntityHeaderWatchlist as LegacyEntityHeaderWatchlist } from '@terminal/components/entity-header-watchlist/EntityHeaderWatchlist';
import { NEW_YORK_TIMEZONE } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { EarningsMarketReactionPrice } from '@terminal/modules/market-reaction-live-price/earnings-market-reaction-price/EarningsMarketReactionPrice';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { EntityHeaderWatchlist as RedesignedEntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import clsx from 'clsx';
import { getDate, getMonth, getYear } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import { LineData } from '../types';
import styles from './EarningsMarketReactionChart.module.scss';

export interface EarningsMarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  line: LineData;
  entity?: Entity;
  hideEntityHeader?: boolean;
  isRedesignedHeader?: boolean;
  isDailyFallback?: boolean;
  watchlistTrackingSource?: PosthogTrackingSource;
}

export const EarningsMarketReactionChart = ({
  fromDateEST,
  toDateEST,
  line,
  entity,
  hideEntityHeader = false,
  isRedesignedHeader = false,
  isDailyFallback = false,
  watchlistTrackingSource,
}: EarningsMarketReactionChartProps) => {
  const { t } = useTranslation(['insights', 'chart']);

  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.positive.value,
    negative: colors.threshold.negative.value,
    neutral: colors.threshold.neutral.value,
  };

  if (!entity || !toDateEST) {
    return null;
  }

  const getVerticalLines = () => {
    if (!fromDateEST || !toDateEST || isDailyFallback) {
      return [];
    }

    return [
      {
        x: fromDateEST,
      },
      {
        x: new Date(
          getYear(fromDateEST),
          getMonth(fromDateEST),
          getDate(fromDateEST),
          16,
          0,
          0,
          0
        ),
        isDashed: true,
      },
      {
        x: new Date(
          getYear(toDateEST),
          getMonth(toDateEST),
          getDate(toDateEST),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: toDateEST,
        isDashed: true,
      },
    ];
  };

  const chartLine = {
    data: line.data.map(item => ({
      date: isDailyFallback
        ? new Date(item.time)
        : toZonedTime(new Date(item.time), NEW_YORK_TIMEZONE),
      value: item.value,
    })),
    label: formatTicker(entity),
    color: colors.line.default.value,
  };

  return (
    <div className={styles.container}>
      <div
        className={clsx(styles.chartTitleWrapper, {
          [styles.priceOnlyHeader]: hideEntityHeader,
          [styles.redesignedHeader]: isRedesignedHeader,
        })}
      >
        {!hideEntityHeader &&
          (isRedesignedHeader ? (
            <RedesignedEntityHeaderWatchlist
              entity={entity}
              trackingSource={watchlistTrackingSource}
              variant="compact"
              watchlistButtonTestId="earnings-chart-watchlist-button"
            />
          ) : (
            <LegacyEntityHeaderWatchlist entity={entity} />
          ))}
        <EarningsMarketReactionPrice
          entity={entity}
          eventProportionChange={line.lastProportionChange}
          lastKnownPrice={line.lastKnownPrice}
          lastKnownDate={line.lastKnownDate}
          prevCloseData={line.prevCloseData}
          isDailyFallback={isDailyFallback}
        />
      </div>
      <PriceChangesChart
        fromDateEST={fromDateEST}
        toDateEST={toDateEST}
        lines={[chartLine]}
        hasTooltip
        tooltipFormatType={'default2f'}
        fillColors={fillColors}
        useTimeScale={isDailyFallback}
        useCalendarDateAxis={isDailyFallback}
        yAxisFormatType="default"
        verticalLines={getVerticalLines()}
        prevClosePrice={line.prevCloseData?.close}
        threshold={line.prevCloseData?.close}
        thresholdLabel={
          line.prevCloseData?.time
            ? t('chart:priceChangesClose', {
                date: formatInTimeZone(line.prevCloseData.time, 'UTC', 'MMM d'),
              })
            : undefined
        }
      />
    </div>
  );
};
